import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [notification, setNotification] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('bookCart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bookCart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (book) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.key === book.key);
      if (existingItem) {
        return prevCart.map((item) =>
          item.key === book.key
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...book, quantity: 1 }];
    });
    setNotification({ title: book.title });
  };

  const removeFromCart = (bookKey) => {
    setCart((prevCart) => prevCart.filter((item) => item.key !== bookKey));
  };

  const updateQuantity = (bookKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(bookKey);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.key === bookKey ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    // Clear localStorage immediately
    try {
      localStorage.removeItem('bookCart');
      // Also set to empty array to ensure it's cleared
      localStorage.setItem('bookCart', JSON.stringify([]));
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.quantity * (item.price || 0)), 0);
  };

  useEffect(() => {
    if (!notification) {
      return;
    }
    const timer = setTimeout(() => setNotification(null), 3000);
    return () => clearTimeout(timer);
  }, [notification]);

  const clearNotification = () => {
    setNotification(null);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        notification,
        clearNotification,
        getCartCount,
        getCartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

